import React, { useEffect, useState, useCallback } from "react";
import { MdSend } from "react-icons/md";
import { useToast } from "../common/ToastContext";
import { useLoading } from "../common/LoadingContext";
import { getReleaseNotesByTopicKey } from "../../services/topicService";
import SearchBox from "../common/SearchBox";

const searchPlaceholder = "Search versions...";

const PromptExportModal = ({
  topicKey,
  topicName,
  topicVersionsList,
  onClose,
  onExport,
}) => {
  const [filteredTopicVersions, setFilteredTopicVersions] = useState(
    topicVersionsList || []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [filteredReleaseNotes, setFilteredReleaseNotes] = useState([]);
  const { setIsLoading } = useLoading();
  const { showToast } = useToast();

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      setIsLoading(true);
      try {
        const notes = await getReleaseNotesByTopicKey(topicKey);
        setReleaseNotes(notes.release_notes || []);
        setFilteredReleaseNotes(notes.release_notes || []);
      } catch (error) {
        console.error("Error fetching release notes:", error);
        showToast("Error fetching release notes", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReleaseNotes();
  }, [topicKey, setIsLoading, showToast]);

  const filterVersions = (term) => {
    if (!term) {
      setFilteredTopicVersions(topicVersionsList);
      setFilteredReleaseNotes(releaseNotes);
      return;
    }
    const lowerTerm = term.toLowerCase();

    const filteredReleaseNotes = Object.fromEntries(
      Object.entries(releaseNotes).filter(
        ([version, notes]) =>
          version.toString().toLowerCase().startsWith(lowerTerm) ||
          notes.comment.toString().toLowerCase().includes(lowerTerm) ||
          notes.by.toString().toLowerCase().includes(lowerTerm)
      )
    );
    setFilteredReleaseNotes(filteredReleaseNotes);
    const filteredTopicVersions = topicVersionsList.filter(([version, _]) =>
      Object.keys(filteredReleaseNotes).includes(version.toString())
    );
    setFilteredTopicVersions(filteredTopicVersions);
  };

  const handleSearch = useCallback(
    (term, _) => {
      setSearchTerm(term);
      filterVersions(term);
    },
    [filterVersions]
  );

  const handleClose = () => {
    onClose();
  };

  const handleSelectVersion = (version) => {
    setSelectedVersions((prev) =>
      prev.includes(version)
        ? prev.filter((v) => v !== version)
        : [...prev, version]
    );
  };

  const handleExport = () => {
    if (selectedVersions.length === 0) {
      showToast("Please select at least one version to export.", "error");
      return;
    }
    const ids = topicVersionsList
      .filter((v) => selectedVersions.includes(v[0].toString()))
      .map((v) => v[1]);
    onExport(ids);
  };

  const handleClearSelected = () => {
    setSelectedVersions([]);
  };

  const containsVersion = (version) => {
    return selectedVersions.includes(version);
  };

  const versionString = () => {
    if (selectedVersions.length === 0) return "";
    if (selectedVersions.length === 1)
      return "Version - " + selectedVersions[0];
    return "Versions - " + selectedVersions.join(", ");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto z-[8900]">
      <div className="bg-gray-800 p-6 rounded-lg w-[650px] m-4">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center justify-between">
          <div>Export {topicName}</div>
        </h2>
        <div className="text-medium font-medium text-white mb-2 flex justify-between">
          <div>{versionString() || "Select Versions to Export"}</div>
          <div>
            <button
              onClick={handleClearSelected}
              className="ml-4 p-2 text-xs font-xs rounded-full bg-red-600 text-white hover:bg-red-700"
            >
              Clear Selected
            </button>
          </div>
        </div>
        <div className="flex justify-between w-full mb-2">
          <div className="w-full">
            <SearchBox
              value={searchTerm}
              onSearch={handleSearch}
              placeholder={searchPlaceholder}
            />
            <ul className="max-h-60 overflow-y-auto mt-2 bg-gray-700 rounded">
              <li className="p-2 border-b border-gray-600 bg-gray-600 text-white font-medium whitespace-nowrap">
                <table>
                  <thead>
                    <tr>
                      <th className="p-2 text-left">Version</th>
                      <th className="p-2 text-left">Commit Message</th>
                      <th className="p-2 text-left">Created At</th>
                      <th className="p-2 text-left">Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(filteredReleaseNotes)
                      .sort((a, b) => parseInt(b) - parseInt(a))
                      .map(([version, note]) => (
                        <tr
                          key={version}
                          onClick={() => handleSelectVersion(version)}
                          className={`cursor-pointer hover:bg-gray-500 ${
                            containsVersion(version) ? "bg-gray-500" : ""
                          }`}
                        >
                          <td className="p-2 border-b border-gray-600 font-light">
                            {version}
                          </td>
                          <td className="p-2 border-b border-gray-600 font-light">
                            {note.comment || "No comment"}
                          </td>
                          <td className="p-2 border-b border-gray-600 font-light">
                            {new Date(note.at).toLocaleDateString()}
                          </td>
                          <td className="p-2 border-b border-gray-600 font-light">
                            {note.by || "Unknown"}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </li>
              {filteredTopicVersions.length === 0 && (
                <li className="p-2 text-gray-400">No versions found.</li>
              )}
            </ul>
          </div>
        </div>
        <div className="flex justify-between w-full">
          <button
            type="submit"
            onClick={handleClose}
            className="ml-4 p-2 rounded-full bg-red-600 text-white hover:bg-red-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleExport}
            disabled={!selectedVersions.length}
            className={`p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 ml-4 ${
              !selectedVersions.length ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <span className="flex items-center">
              <MdSend /> Export
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptExportModal;
